/* eslint-disable react-hooks/exhaustive-deps */
/**
 * V2TemplateDrawer — "Compose Studio".
 *
 * Focused image builder for cards in render_mode='template'. The user picks a
 * layout, fills the image slots (background + cut-out subject, with recolour),
 * then "Save to Director" flattens the composed image into the card's plate,
 * preserves the text/logo overlay, and switches the card back to the freeform
 * Director editor (render_mode=None) where text + logos are added.
 *
 * Sections: 1) layout picker, 2) image slots, preview, cut-out matte warning,
 * 3) Save to Director.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Badge } from "./ui/badge";
import { Skeleton } from "./ui/skeleton";
import { Card, CardContent } from "./ui/card";
import { Loader2, AlertTriangle, CheckCircle2, ImageIcon, ImageOff, Upload, Sparkles, Palette, Search, Shuffle, Pencil, Trash2, Grid3x3, Wand2 } from "lucide-react";
import { toast } from "sonner";

const _API_BASE =
  typeof window !== "undefined" && window.location.hostname !== "localhost"
    ? window.location.origin
    : process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const API = `${_API_BASE}/api`;

// 350ms debounce for slot edits → live preview refresh
const PREVIEW_DEBOUNCE_MS = 350;

// Brand-asset pools are shared across the many SlotImagePicker instances. Cache
// + de-dupe them so a drawer with N image slots fires ONE request per pool, not
// N. Loading is also lazy (only when the Library tab is actually viewed).
const _LIB_CACHE = new Map();     // `${ws}:${pool}` -> assets[]
const _LIB_INFLIGHT = new Map();  // `${ws}:${pool}` -> Promise<assets[]>
const fetchPool = (ws, pool, force = false) => {
  const key = `${ws}:${pool}`;
  if (force) { _LIB_CACHE.delete(key); _LIB_INFLIGHT.delete(key); }
  if (_LIB_CACHE.has(key)) return Promise.resolve(_LIB_CACHE.get(key));
  if (_LIB_INFLIGHT.has(key)) return _LIB_INFLIGHT.get(key);
  const p = fetch(`${API}/v2/workspaces/${ws}/brand/assets?pool=${pool}`, { credentials: "include" })
    .then((r) => r.json())
    .then((d) => { const arr = Array.isArray(d) ? d : []; _LIB_CACHE.set(key, arr); _LIB_INFLIGHT.delete(key); return arr; })
    .catch(() => { _LIB_INFLIGHT.delete(key); return []; });
  _LIB_INFLIGHT.set(key, p);
  return p;
};

export default function V2TemplateDrawer({ open, onOpenChange, workspaceId, cardId, onMutated, onSwitchToDirector }) {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingToDirector, setSavingToDirector] = useState(false);
  const [snapThirds, setSnapThirds] = useState(false);
  const [previewLoaded, setPreviewLoaded] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [previewVersion, setPreviewVersion] = useState(0);
  const [quality, setQuality] = useState(null);
  const [qualityLoading, setQualityLoading] = useState(false);
  const debounceRef = useRef(null);
  const cardRef = useRef(null);          // always-fresh card (avoids stale closures)
  const patchChainRef = useRef(Promise.resolve()); // strict-serialised PATCH chain
  const pendingBodyRef = useRef(null);   // latest debounced body not yet enqueued
  const pendingCountRef = useRef(0);     // in-flight chained patches (for saving spinner)

  // ─── "Rex, design this" auto-compose (same agent as the Director) ──────
  const [autoComposing, setAutoComposing] = useState(false);
  const [autoStatus, setAutoStatus] = useState("");
  const [variantOptions, setVariantOptions] = useState([]); // [{variant_id,label,rationale,image_url}]
  const [applyingVariant, setApplyingVariant] = useState(null);
  const [briefText, setBriefText] = useState("");

  // Re-fetch the card and bump the live preview after Rex composes.
  const reloadCard = async () => {
    try {
      const res = await fetch(`${API}/v2/workspaces/${workspaceId}/cards/${cardId}`, { credentials: "include" }).then((r) => r.json());
      const c = res?.card || res;
      cardRef.current = c;
      setCard(c);
      setPreviewVersion((v) => v + 1);
    } catch (e) { /* preview will catch up on next edit */ }
  };

  const runAutoCompose = async () => {
    if (autoComposing) return;
    setAutoComposing(true);
    setVariantOptions([]);
    setAutoStatus("Rex is planning a few options…");
    try {
      const r = await fetch(
        `${API}/v2/workspaces/${workspaceId}/cards/${cardId}/auto-compose`,
        { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ variants: 3, ...(briefText.trim() ? { brief: briefText.trim() } : {}) }) }
      );
      const started = await r.json().catch(() => ({}));
      if (!r.ok || !started.job_id) {
        toast.error(started?.detail || "Rex couldn't start the design");
        setAutoComposing(false); setAutoStatus(""); return;
      }
      setAutoStatus(started?.plan?.rationale || "Rendering 3 variants to choose from…");
      const deadline = Date.now() + 150000;
      let miss = 0; // consecutive poll misses (404 / network) — tolerate briefly
      while (Date.now() < deadline) {
        await new Promise((res) => setTimeout(res, 2000));
        let sr, sj;
        try {
          sr = await fetch(
            `${API}/v2/workspaces/${workspaceId}/cards/${cardId}/image/from-compose-status/${started.job_id}`,
            { credentials: "include" }
          );
          sj = await sr.json().catch(() => ({}));
        } catch { miss += 1; if (miss > 8) break; continue; }
        // A 404 early just means the job row isn't visible yet — keep waiting; but
        // if it persists, fail clearly instead of spinning to the 150s timeout.
        if (sr.status === 404) {
          miss += 1;
          if (miss > 8) { toast.error("Lost track of Rex's design — please try again"); break; }
          continue;
        }
        miss = 0;
        if (sj.status === "done") {
          const vs = sj.variants || [];
          if (vs.length) {
            setVariantOptions(vs);
            if (sj.degraded || (sj.built && sj.built < (sj.requested || 3)))
              toast.message(`Rex drafted ${vs.length} option${vs.length > 1 ? "s" : ""} — a layer couldn't be saved; retry for the full set`);
            else
              toast.success(`Rex drafted ${vs.length} options — pick your favourite`);
          } else {
            toast.success("Rex designed your graphic — tweak the layout below");
            await reloadCard();
            onMutated && onMutated();
          }
          setAutoComposing(false); setAutoStatus(""); return;
        }
        if (sj.status === "error") {
          toast.error(sj?.detail || "Rex's compose failed");
          setAutoComposing(false); setAutoStatus(""); return;
        }
        if (sj.status === "processing") {
          setAutoStatus(briefText.trim()
            ? "Rendering your variants — generating & cutting layers (~30-60s)…"
            : "Rendering 3 variants…");
        }
      }
      toast.error("Rex's design timed out — please try again");
    } catch (e) {
      toast.error("Rex couldn't design this right now");
    } finally {
      setAutoComposing(false); setAutoStatus("");
    }
  };

  const applyVariant = async (variantId) => {
    if (applyingVariant) return;
    setApplyingVariant(variantId);
    try {
      const r = await fetch(
        `${API}/v2/workspaces/${workspaceId}/cards/${cardId}/auto-compose/apply`,
        { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ variant_id: variantId, refine: true }) }
      );
      const started = await r.json().catch(() => ({}));
      if (!r.ok || !started.job_id) {
        toast.error(started?.detail || "Couldn't apply that variant");
        setApplyingVariant(null); return;
      }
      const deadline = Date.now() + 90000;
      while (Date.now() < deadline) {
        await new Promise((res) => setTimeout(res, 2000));
        const sr = await fetch(
          `${API}/v2/workspaces/${workspaceId}/cards/${cardId}/image/from-compose-status/${started.job_id}`,
          { credentials: "include" }
        );
        const sj = await sr.json().catch(() => ({}));
        if (sj.status === "done" || sj.status === "error") break;
      }
      toast.success("Applied — opening the Director to add text & logos");
      setVariantOptions([]);
      onMutated && onMutated();
      // apply flips render_mode → Director (None); move the user there to finish.
      if (onSwitchToDirector) onSwitchToDirector(cardId);
      else await reloadCard();
    } catch (e) {
      toast.error("Couldn't apply that variant");
    } finally {
      setApplyingVariant(null);
    }
  };


  // ─── Fetch card + templates whenever the drawer opens for a new id ────
  useEffect(() => {
    if (!open || !cardId || !workspaceId) return;
    setVariantOptions([]);
    setAutoStatus("");
    setBriefText("");
    setAutoComposing(false);
    setLoading(true);
    Promise.all([
      fetch(`${API}/v2/workspaces/${workspaceId}/cards/${cardId}`, { credentials: "include" }).then((r) => r.json()),
      fetch(`${API}/v2/workspaces/${workspaceId}/templates`, { credentials: "include" }).then((r) => r.json()),
    ])
      .then(([cardRes, tplRes]) => {
        const c = cardRes?.card || cardRes;
        cardRef.current = c;
        setCard(c);
        setTemplates(Array.isArray(tplRes) ? tplRes : []);
      })
      .catch(() => toast.error("Failed to load card"))
      .finally(() => setLoading(false));
  }, [open, cardId, workspaceId]);

  // ─── Recompute quality when template/slot_values change ───────────────
  const refreshQuality = () => {
    if (!cardId) return;
    setQualityLoading(true);
    fetch(`${API}/v2/workspaces/${workspaceId}/cards/${cardId}/quality`, { credentials: "include" })
      .then((r) => r.json())
      .then(setQuality)
      .catch(() => {})
      .finally(() => setQualityLoading(false));
  };
  useEffect(() => {
    if (open && card?.template_id) refreshQuality();
  }, [open, card?.template_id, previewVersion]);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.template_id === card?.template_id),
    [templates, card?.template_id]
  );

  const imageSlots = useMemo(
    () => (selectedTemplate?.slots || []).filter(
      (s) => s.type === "image_cutout" || s.type === "image_full"
    ),
    [selectedTemplate]
  );

  const previewUrl = card?.template_id
    ? `${API}/v2/workspaces/${workspaceId}/cards/${cardId}/render-preview?width=1080&height=1080&chrome=false&_v=${previewVersion}`
    : null;

  // All cut-out subject slots that have an image — each is independently
  // draggable/resizable on the preview (multi-object Compose Studio).
  const subjectSlots = useMemo(
    () => imageSlots.filter((s) => s.type === "image_cutout" && (card?.slot_values?.[s.id]?.storage_path)),
    [imageSlots, card?.slot_values]
  );

  // ─── Patch helpers (strict promise-chain serialization) ────────────────
  // Every card PATCH is chained after the previous one and reads the FRESH
  // version_id (cardRef, updated from each response) at send time — so two
  // edits can never collide on a stale version (the prior code raced → 409
  // conflicts that stalled Save-to-Director). On a 409 we refetch + retry once.
  const _doPatch = async (body, expectedVersion) => {
    const r = await fetch(`${API}/v2/workspaces/${workspaceId}/cards/${cardId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ ...body, expected_version: expectedVersion }),
    });
    const j = await r.json().catch(() => ({}));
    return { r, j };
  };

  const _sendPatchNow = async (body) => {
    let { r, j } = await _doPatch(body, cardRef.current?.version || 1);
    if (r.status === 409) {
      try {
        const fresh = await fetch(`${API}/v2/workspaces/${workspaceId}/cards/${cardId}`, { credentials: "include" }).then((x) => x.json());
        const fc = fresh?.card || fresh;
        if (fc) cardRef.current = fc;
      } catch { /* ignore */ }
      ({ r, j } = await _doPatch(body, cardRef.current?.version || 1));
    }
    if (!r.ok) throw new Error(j?.detail || `Save failed (${r.status})`);
    const updated = j.card || j;
    cardRef.current = updated;
    setCard(updated);
    setPreviewVersion((v) => v + 1);
    onMutated?.();
  };

  // Chain a patch; returns a promise that resolves when THIS patch is done.
  const enqueuePatch = (body) => {
    pendingCountRef.current += 1;
    setSaving(true);
    const run = patchChainRef.current
      .catch(() => {})
      .then(() => _sendPatchNow(body))
      .catch((e) => { toast.error(e?.message || "Save failed"); })
      .finally(() => {
        pendingCountRef.current = Math.max(0, pendingCountRef.current - 1);
        if (pendingCountRef.current === 0) setSaving(false);
      });
    patchChainRef.current = run;
    return run;
  };

  const queuePatch = (body) => {
    pendingBodyRef.current = { ...(pendingBodyRef.current || {}), ...body };
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const b = pendingBodyRef.current; pendingBodyRef.current = null; debounceRef.current = null;
      if (b) enqueuePatch(b);
    }, PREVIEW_DEBOUNCE_MS);
  };

  // Flush any debounced edit immediately and wait for the whole chain to idle.
  const flushAllPatches = async () => {
    if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
    if (pendingBodyRef.current) {
      const b = pendingBodyRef.current; pendingBodyRef.current = null;
      enqueuePatch(b);
    }
    await Promise.race([
      patchChainRef.current.catch(() => {}),
      new Promise((res) => setTimeout(res, 10000)),
    ]);
  };

  // Immediate, awaited patch (template pick).
  const patchCard = async (body) => {
    await enqueuePatch(body);
  };

  // Like enqueuePatch but RE-THROWS on failure (callers that need to react —
  // e.g. revert optimistic UI — must know the patch failed instead of it being
  // silently toasted-and-swallowed).
  const patchCardStrict = (body) => {
    pendingCountRef.current += 1;
    setSaving(true);
    const run = patchChainRef.current
      .catch(() => {})
      .then(() => _sendPatchNow(body))
      .finally(() => {
        pendingCountRef.current = Math.max(0, pendingCountRef.current - 1);
        if (pendingCountRef.current === 0) setSaving(false);
      });
    patchChainRef.current = run.catch(() => {}); // keep the chain alive on failure
    return run;                                   // but surface failure to caller
  };

  const setSlotValue = (slotId, value) => {
    const base = cardRef.current?.slot_values || {};
    const next = { ...base, [slotId]: value };
    cardRef.current = cardRef.current ? { ...cardRef.current, slot_values: next } : cardRef.current;
    setCard((c) => (c ? { ...c, slot_values: next } : c));
    queuePatch({ slot_values: next });
  };

  // Pick a layout. We update the UI OPTIMISTICALLY so the picker reflects the
  // choice instantly (no "nothing happens" lag), then persist. On a version
  // conflict the chained PATCH re-applies THIS template_id (the body is fixed,
  // so it can never resolve back to the default); only a hard failure reverts.
  const onPickTemplate = async (tid) => {
    if ((cardRef.current?.template_id || card?.template_id) === tid) return;
    const prev = cardRef.current;
    const optimistic = { ...(cardRef.current || {}), template_id: tid, render_mode: "template" };
    cardRef.current = optimistic;
    setCard(optimistic);
    setPreviewVersion((v) => v + 1);
    try {
      await patchCardStrict({ template_id: tid, render_mode: "template" });
    } catch (e) {
      if (prev) { cardRef.current = prev; setCard(prev); setPreviewVersion((v) => v + 1); }
      toast.error("Couldn't switch layout — reverted");
    }
  };

  // ─── Save to Director ──────────────────────────────────────────────────
  // Flattens the composed image (background + cut-out subject) into the card's
  // plate, preserves the text/logo overlay, flips render_mode back to the
  // Director, and switches the user into the freeform editor.
  const saveToDirector = async () => {
    if (!cardRef.current?.template_id && !card?.template_id) return toast.error("Pick a layout first");
    setSavingToDirector(true);
    const tId = toast.loading("Flattening image & cutting out the subjects… (~20s)");
    try {
      // Persist the latest slot_values authoritatively before rendering.
      // Chain a final patch (so it never races in-flight ones) and await THAT
      // promise with a hard timeout so a stuck chain can never block the save.
      if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
      if (pendingBodyRef.current) { const b = pendingBodyRef.current; pendingBodyRef.current = null; enqueuePatch(b); }
      const finalPatch = enqueuePatch({ slot_values: cardRef.current?.slot_values || card?.slot_values || {} });
      await Promise.race([
        finalPatch,
        new Promise((res) => setTimeout(res, 10000)),
      ]);
      // Render runs as a BACKGROUND job (rembg per subject is CPU-bound and can
      // take ~40s for 5 subjects — too long for one request behind the ingress).
      // POST returns a job_id immediately, then we poll until done.
      const r = await fetch(
        `${API}/v2/workspaces/${workspaceId}/cards/${cardId}/image/from-compose`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ width: 1080, height: 1080 }),
        }
      );
      const started = await r.json().catch(() => ({}));
      if (!r.ok || !started?.job_id) throw new Error(started?.detail || `Save failed (${r.status})`);
      // Poll for completion (up to ~3 min).
      let done = null;
      for (let i = 0; i < 90; i++) {
        await new Promise((res) => setTimeout(res, 2000));
        const sr = await fetch(
          `${API}/v2/workspaces/${workspaceId}/cards/${cardId}/image/from-compose-status/${started.job_id}`,
          { credentials: "include" }
        );
        const sj = await sr.json().catch(() => ({}));
        if (sj?.status === "done") { done = sj; break; }
        if (sj?.status === "error") throw new Error(sj?.detail || "Compose failed");
      }
      if (!done) throw new Error("Rendering timed out — please retry");
      toast.success("Saved to Director — drag the subjects & add text/logos there", { id: tId });
      onMutated?.();
      onOpenChange?.(false);
      onSwitchToDirector?.(cardId);
    } catch (e) {
      toast.error(e?.message || "Could not save to Director — please retry", { id: tId });
    } finally {
      setSavingToDirector(false);
    }
  };

  // Matte (cut-out) warnings only — surfaced from the quality endpoint.
  const matteWarnings = (quality?.issues || []).filter((i) =>
    String(i.code || "").startsWith("matte_")
  );

  // ─── Render helpers ────────────────────────────────────────────────────
  const renderSlotEditor = (slot) => {
    const sv = card?.slot_values?.[slot.id] || {};
    const stype = slot.type;
    const isText = stype === "text" || stype === "text_rich";
    const isImage = stype === "image_cutout" || stype === "image_full";
    const isAsset = stype === "asset_pin" || stype === "asset_decoration" || stype === "asset_scatter";

    return (
      <Card key={slot.id} data-testid={`slot-editor-${slot.id}`} className="p-3">
        <CardContent className="p-0 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="font-mono text-xs">{slot.id}</Badge>
              <span className="text-xs text-muted-foreground">{stype}</span>
              {slot.required ? <Badge variant="destructive" className="text-xs">required</Badge> : null}
              {slot.role ? <span className="text-xs text-muted-foreground">role={slot.role}</span> : null}
            </div>
            {slot.max_chars ? (
              <span className="text-xs text-muted-foreground">
                {(sv?.text || "").length}/{slot.max_chars}
              </span>
            ) : null}
          </div>

          {isText ? (
            slot.max_lines && slot.max_lines > 1 ? (
              <Textarea
                data-testid={`slot-text-${slot.id}`}
                value={sv?.text || ""}
                rows={Math.min(6, slot.max_lines)}
                placeholder={typeof slot.default_value === "string" ? slot.default_value : `Enter ${slot.id}...`}
                onChange={(e) => setSlotValue(slot.id, { ...sv, text: e.target.value })}
              />
            ) : (
              <Input
                data-testid={`slot-text-${slot.id}`}
                value={sv?.text || ""}
                placeholder={typeof slot.default_value === "string" ? slot.default_value : `Enter ${slot.id}...`}
                onChange={(e) => setSlotValue(slot.id, { ...sv, text: e.target.value })}
              />
            )
          ) : null}

          {isImage ? (
            <ImageSlotEditor
              workspaceId={workspaceId}
              cardId={cardId}
              slot={slot}
              currentValue={sv}
              onValueChange={(v) => setSlotValue(slot.id, v)}
            />
          ) : null}

          {isAsset ? (
            <p className="text-xs text-muted-foreground italic">
              {stype === "asset_scatter"
                ? "Procedural scatter — seeded from template default. Edit advanced settings in the brand admin."
                : "Asset slot — configurable in the brand admin."}
            </p>
          ) : null}
        </CardContent>
      </Card>
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="v2-template-drawer"
        side="right"
        className="w-full sm:max-w-3xl overflow-y-auto"
      >
        <SheetHeader>
          <SheetTitle data-testid="drawer-title" className="flex items-center gap-3">
            <Palette className="h-5 w-5 text-blue-600" />
            <span className="truncate">Compose Studio</span>
            {saving ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : null}
            <Badge variant="outline" className="ml-auto" data-testid="drawer-stage">
              {card?.stage || "—"}
            </Badge>
          </SheetTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Build the image — pick a layout, drop in a background and a cut-out subject. Add text & logos in the Director after saving.
          </p>
        </SheetHeader>

        {loading ? (
          <div className="space-y-3 py-6">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-72 w-full" />
          </div>
        ) : (
          <div className="space-y-6 py-4">
            {/* 0 — "Rex, design this" auto-compose (same agent as the Director) */}
            {card?.card_type !== "COVER_DESIGN" && (
              <section
                className="rounded-md border border-indigo-200 bg-indigo-50/60 p-3"
                data-testid="rex-auto-compose"
              >
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-semibold text-indigo-900 flex items-center gap-1.5">
                      <Wand2 className="h-4 w-4" /> Rex, design this
                    </div>
                    <p className="text-[11px] text-indigo-700/80 mt-0.5">
                      One tap — Rex picks a layout, cuts out a subject, adds a shadow and styles the headline. Tweak it below after.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={runAutoCompose}
                    disabled={autoComposing}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white shrink-0"
                    data-testid="rex-auto-compose-btn"
                  >
                    {autoComposing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Wand2 className="h-3.5 w-3.5 mr-1.5" />Design it</>}
                  </Button>
                </div>
                <textarea
                  value={briefText}
                  onChange={(e) => setBriefText(e.target.value)}
                  disabled={autoComposing}
                  rows={2}
                  placeholder="Optional brief for Rex — e.g. 'a jigsaw puzzle coming together, a cut-out of a facilitator on the right, DISC Lens logo bottom-right'. Rex can generate elements, cut stock props, use your subjects & logos."
                  className="mt-2 w-full text-[11px] rounded-md border border-indigo-200 bg-white/80 p-2 text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-400 resize-none"
                  data-testid="rex-brief-input"
                />
                {autoComposing && autoStatus && (
                  <div className="mt-2 flex items-center gap-1.5 text-[11px] text-indigo-700" data-testid="rex-auto-compose-status">
                    <Loader2 className="h-3 w-3 animate-spin" /> {autoStatus}
                  </div>
                )}
                {variantOptions.length > 0 && (
                  <div className="mt-3" data-testid="rex-variant-picker">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="text-[11px] font-semibold text-indigo-900">
                        Pick a variant — Rex will polish your choice
                      </div>
                      <button
                        type="button"
                        onClick={() => setVariantOptions([])}
                        disabled={!!applyingVariant}
                        className="text-[10px] text-slate-500 hover:text-indigo-700 disabled:opacity-50"
                        data-testid="rex-variant-picker-dismiss"
                      >
                        Dismiss
                      </button>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {variantOptions.map((v) => (
                        <button
                          key={v.variant_id}
                          type="button"
                          onClick={() => applyVariant(v.variant_id)}
                          disabled={!!applyingVariant}
                          className="group text-left rounded-md border border-indigo-200 bg-white overflow-hidden hover:border-indigo-500 hover:ring-2 hover:ring-indigo-300 transition-colors disabled:opacity-60"
                          data-testid={`rex-variant-${v.variant_id}`}
                        >
                          <div className="relative aspect-square bg-slate-100">
                            {v.image_url ? (
                              <img src={v.image_url} alt={v.label} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-slate-400 text-xs">…</div>
                            )}
                            {applyingVariant === v.variant_id && (
                              <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                                <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
                              </div>
                            )}
                          </div>
                          <div className="p-1.5">
                            <div className="text-[11px] font-semibold text-indigo-900 leading-tight">{v.label}</div>
                            <div className="text-[10px] text-slate-500 leading-snug mt-0.5 line-clamp-2">{v.rationale}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            )}

            {/* 1 — Choose a layout */}
            <section data-testid="template-picker">
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">1 · Choose a layout</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {templates.map((t) => (
                  <button
                    key={t.template_id}
                    data-testid={`template-card-${t.template_id}`}
                    onClick={() => onPickTemplate(t.template_id)}
                    className={`text-left rounded-md border p-2 transition hover:border-blue-500 ${
                      t.template_id === card?.template_id ? "border-blue-600 ring-2 ring-blue-200" : "border-border"
                    }`}
                  >
                    <div className="font-medium text-sm truncate">{t.name}</div>
                  </button>
                ))}
              </div>
            </section>

            {/* 2 — Fill the image slots */}
            <section data-testid="slot-editor">
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                2 · Background &amp; subject
              </h3>
              <div className="space-y-2">
                {imageSlots.map(renderSlotEditor)}
                {!card?.template_id ? (
                  <p className="text-sm text-muted-foreground italic">
                    Choose a layout above to start adding images.
                  </p>
                ) : !imageSlots.length ? (
                  <p className="text-sm text-muted-foreground italic">
                    This layout has no image slots — it's text-only. Pick a layout with a background or subject.
                  </p>
                ) : null}
              </div>
            </section>

            {/* Live preview */}
            <section data-testid="live-preview">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Preview</h3>
                {subjectSlots.length ? (
                  <button
                    type="button"
                    data-testid="snap-thirds-toggle"
                    onClick={() => setSnapThirds((v) => !v)}
                    className={`inline-flex items-center gap-1 text-[11px] rounded border px-2 py-0.5 transition ${
                      snapThirds ? "border-blue-600 bg-blue-50 text-blue-800" : "border-border text-muted-foreground hover:border-blue-300"
                    }`}
                    title="Snap the subject to the rule-of-thirds lines"
                  >
                    <Grid3x3 className="h-3 w-3" /> Thirds {snapThirds ? "on" : "off"}
                  </button>
                ) : null}
              </div>
              {previewUrl ? (
                <>
                  <div className="relative rounded-lg overflow-hidden border select-none">
                    <img
                      data-testid="live-preview-img"
                      src={previewUrl}
                      alt="Live preview"
                      className="w-full h-auto block bg-zinc-100"
                      draggable={false}
                      onLoad={() => setPreviewLoaded(true)}
                    />
                    {snapThirds ? (
                      <div className="absolute inset-0 pointer-events-none z-[5]" data-testid="thirds-grid">
                        <div className="absolute top-0 bottom-0 border-l border-white/50" style={{ left: "33.33%" }} />
                        <div className="absolute top-0 bottom-0 border-l border-white/50" style={{ left: "66.66%" }} />
                        <div className="absolute left-0 right-0 border-t border-white/50" style={{ top: "33.33%" }} />
                        <div className="absolute left-0 right-0 border-t border-white/50" style={{ top: "66.66%" }} />
                      </div>
                    ) : null}
                    {previewLoaded ? subjectSlots.map((ss, i) => (
                      <SubjectTransformLayer
                        key={ss.id}
                        slot={ss}
                        index={i}
                        value={card?.slot_values?.[ss.id] || {}}
                        snap={snapThirds}
                        onChange={(patch) =>
                          setSlotValue(ss.id, { ...(card?.slot_values?.[ss.id] || {}), ...patch })
                        }
                      />
                    )) : null}
                    {saving ? (
                      <div className="absolute top-2 right-2 bg-white/90 rounded px-2 py-1 text-xs flex items-center gap-1 z-10">
                        <Loader2 className="h-3 w-3 animate-spin" /> rendering…
                      </div>
                    ) : null}
                  </div>
                  {subjectSlots.length ? (
                    <p className="text-[11px] text-muted-foreground mt-1" data-testid="subject-drag-hint">
                      {subjectSlots.length > 1
                        ? `${subjectSlots.length} subjects — drag each to reposition · drag its corner to resize.`
                        : "Drag the subject to reposition · drag the corner to resize."}
                    </p>
                  ) : null}
                </>
              ) : (
                <div className="text-sm text-muted-foreground italic">Pick a layout to see the preview.</div>
              )}
            </section>

            {/* Cut-out (matte) warnings only */}
            {matteWarnings.length ? (
              <div
                data-testid="matte-warning"
                className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 space-y-1"
              >
                <div className="flex items-center gap-2 font-medium">
                  <AlertTriangle className="h-4 w-4 text-amber-600" /> Cut-out needs a check
                </div>
                {matteWarnings.slice(0, 3).map((w, idx) => (
                  <div key={idx} data-testid={`matte-warning-${w.code}`}>{w.detail}</div>
                ))}
              </div>
            ) : null}

            {/* 3 — Save to Director */}
            <section data-testid="save-to-director-section" className="border-t pt-4">
              <Button
                data-testid="save-to-director-btn"
                className="w-full"
                onClick={saveToDirector}
                disabled={savingToDirector || !card?.template_id}
              >
                {savingToDirector ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Saving…</>
                ) : (
                  <><CheckCircle2 className="h-4 w-4 mr-2" /> Save to Director (add text &amp; logos)</>
                )}
              </Button>
              <p className="text-[11px] text-muted-foreground italic mt-2 text-center">
                Flattens the image into your post and opens the Director. Your existing text &amp; logos are kept.
              </p>
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ─── Image slot editor (Library / Stock / Upload / Generate) ────────────
// ─── Subject free transform (drag + corner resize on the live preview) ────
function SubjectTransformLayer({ slot, value, onChange, snap, index = 0 }) {
  const anchor = slot.anchor || {};
  const size = slot.size || {};
  const defCx = (anchor.x ?? 0) + (size.w ?? 1) / 2;
  const defCy = (anchor.y ?? 0) + (size.h ?? 1) / 2;
  const cx = value.subject_x != null ? value.subject_x : defCx;
  const cy = value.subject_y != null ? value.subject_y : defCy;
  const scale = value.subject_scale != null ? value.subject_scale : 1.0;
  const boxW = Math.min(0.98, (size.w ?? 0.5) * scale);
  const boxH = Math.min(0.98, (size.h ?? 0.5) * scale);
  const label = index === 0 ? "Subject" : `Subject ${index + 1}`;

  const dragRef = useRef(null);

  const snapVal = (v) => {
    if (!snap) return v;
    for (const line of [1 / 3, 0.5, 2 / 3]) {
      if (Math.abs(v - line) < 0.035) return line;
    }
    return v;
  };

  const rectOf = (el) => el.closest(".relative")?.getBoundingClientRect();

  const startMove = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = rectOf(e.currentTarget);
    if (!rect) return;
    const onMove = (ev) => {
      const px = (ev.clientX - rect.left) / rect.width;
      const py = (ev.clientY - rect.top) / rect.height;
      onChange({
        subject_x: snapVal(Math.max(0.02, Math.min(0.98, px))),
        subject_y: snapVal(Math.max(0.02, Math.min(0.98, py))),
      });
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const startResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = rectOf(e.currentTarget);
    if (!rect) return;
    const centerPxX = rect.left + cx * rect.width;
    const centerPxY = rect.top + cy * rect.height;
    const baseHalf = ((size.w ?? 0.5) * rect.width) / 2;
    const onMove = (ev) => {
      const dx = Math.abs(ev.clientX - centerPxX);
      const dy = Math.abs(ev.clientY - centerPxY);
      const half = Math.max(dx, dy);
      const next = Math.max(0.2, Math.min(4.0, half / Math.max(1, baseHalf)));
      onChange({ subject_scale: Math.round(next * 100) / 100 });
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  return (
    <div
      ref={dragRef}
      data-testid={`subject-transform-box-${slot.id}`}
      onPointerDown={startMove}
      className="absolute cursor-move border-2 border-dashed border-blue-500/80 bg-blue-400/10 rounded"
      style={{
        zIndex: 10 + index,
        left: `${(cx - boxW / 2) * 100}%`,
        top: `${(cy - boxH / 2) * 100}%`,
        width: `${boxW * 100}%`,
        height: `${boxH * 100}%`,
      }}
    >
      <span className="absolute -top-5 left-0 text-[10px] font-medium text-blue-700 bg-white/80 px-1 rounded">
        {label}
      </span>
      <div
        data-testid={`subject-resize-handle-${slot.id}`}
        onPointerDown={startResize}
        className="absolute -bottom-2 -right-2 h-4 w-4 rounded-full bg-blue-600 border-2 border-white cursor-nwse-resize"
      />
    </div>
  );
}


function ImageSlotEditor({ workspaceId, cardId, slot, currentValue, onValueChange }) {
  const isCutout = slot.type === "image_cutout";
  const pool = isCutout ? "subjects" : "backgrounds";
  // Cutout slots strip the background (rembg); full-image / background slots keep it.
  // auto_cutout defaults to isCutout, but an explicit value in `v` wins (so the
  // operator can mark a logo / pre-cut PNG as "already cut out", skipping rembg).
  const setVal = (v) => onValueChange({ auto_cutout: isCutout, ...v });

  const nudgeSubject = (dx, dy) => {
    const a = slot.anchor || {};
    const s = slot.size || {};
    const defCx = (a.x ?? 0) + (s.w ?? 1) / 2;
    const defCy = (a.y ?? 0) + (s.h ?? 1) / 2;
    const curX = currentValue?.subject_x != null ? currentValue.subject_x : defCx;
    const curY = currentValue?.subject_y != null ? currentValue.subject_y : defCy;
    setVal({
      ...currentValue,
      subject_x: Math.max(0.02, Math.min(0.98, curX + dx)),
      subject_y: Math.max(0.02, Math.min(0.98, curY + dy)),
    });
  };

  // Default to STOCK (the primary source), NOT the photo library — so opening a
  // slot never dumps a long list of saved library photos. Library only shows
  // when the user explicitly clicks its tab, or when the slot already uses one.
  const [tab, setTab] = useState(currentValue?.source || "stock");
  const [libraryAssets, setLibraryAssets] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [subjectBrief, setSubjectBrief] = useState("");
  const [stockQuery, setStockQuery] = useState("");
  const [stockResults, setStockResults] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [stockPicking, setStockPicking] = useState(null);

  const reloadLibrary = useCallback((force = false) => {
    if (!workspaceId) return;
    fetchPool(workspaceId, pool, force).then(setLibraryAssets);
  }, [workspaceId, pool]);

  // LAZY: only fetch the pool when the Library tab is actually the active source
  // (the brief path and stock/generate/upload tabs don't need it). The cache in
  // fetchPool ensures just ONE request per (workspace, pool) across all slots.
  useEffect(() => {
    if (tab === "library") reloadLibrary();
  }, [tab, reloadLibrary]);

  const renameLibraryAsset = async (assetId, currentLabel) => {
    const label = window.prompt("Rename background", currentLabel || "");
    if (!label || !label.trim()) return;
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/brand/assets/${assetId}/rename`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ label: label.trim() }),
      });
      if (!r.ok) throw new Error("rename failed");
      toast.success("Renamed");
      reloadLibrary(true);
    } catch (e) {
      toast.error(e?.message || "Rename failed");
    }
  };

  const deleteLibraryAsset = async (assetId) => {
    if (!window.confirm("Remove this from the library? Cards already using it keep it.")) return;
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/brand/assets/${assetId}`, {
        method: "DELETE", credentials: "include",
      });
      if (!r.ok) throw new Error("delete failed");
      toast.success("Removed from library");
      reloadLibrary(true);
    } catch (e) {
      toast.error(e?.message || "Delete failed");
    }
  };

  const runStockSearch = async () => {
    if (!stockQuery.trim()) return toast.error("Type something to search");
    setStockLoading(true);
    try {
      const r = await fetch(
        `${API}/v2/workspaces/${workspaceId}/stock/search?q=${encodeURIComponent(stockQuery.trim())}&sources=unsplash,pexels&per_page=12`,
        { credentials: "include" }
      );
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || "Search failed");
      setStockResults(d.candidates || []);
      if (!(d.candidates || []).length) toast.message("No results — try different keywords");
    } catch (e) {
      toast.error(e?.message || "Stock search failed");
    } finally {
      setStockLoading(false);
    }
  };

  const pickStock = async (cand) => {
    setStockPicking(cand.source_id);
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/stock/approve`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          search_query: stockQuery.trim(),
          approvals: [{
            source: cand.source,
            source_id: cand.source_id,
            full_url: cand.full_url,
            pool,
            photographer_name: cand.photographer_name,
            photographer_username: cand.photographer_username,
            photographer_url: cand.photographer_url,
            source_url: cand.source_url,
            description: cand.description,
            attribution_required: cand.attribution_required,
            attribution_text: cand.attribution_text,
            width: cand.width,
            height: cand.height,
          }],
        }),
      });
      const d = await r.json();
      const asset = (d.ingested || [])[0];
      if (!r.ok || !asset) throw new Error((d.failures?.[0]?.error) || d?.detail || "Could not import photo");
      setVal({ source: "stock", asset_id: asset.asset_id, storage_path: asset.storage_path });
      toast.success(isCutout ? "Stock photo added — background will be removed" : "Background photo added");
    } catch (e) {
      toast.error(e?.message || "Failed to add stock photo");
    } finally {
      setStockPicking(null);
    }
  };

  const onUpload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("pool", pool);
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/brand/assets/upload`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      if (!r.ok) throw new Error("upload failed");
      const j = await r.json();
      setVal({ source: "upload", asset_id: j.asset_id, storage_path: j.storage_path });
      toast.success("Uploaded");
    } catch (e) {
      toast.error(e?.message || "Upload failed");
    }
  };

  const onGenerate = async () => {
    if (!subjectBrief.trim()) return toast.error("Describe the subject first");
    setGenerating(true);
    try {
      const r = await fetch(
        `${API}/v2/workspaces/${workspaceId}/cards/${cardId}/image/generate-subject`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            subject_brief: subjectBrief,
            slot_id: slot.id,
            auto_cutout: true,
          }),
        }
      );
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail || "generation failed");
      const verdict = j.realism_verdict?.overall || "pass";
      if (verdict === "fail") toast.warning("Realism gate flagged hard issues — review before publishing");
      else if (verdict === "soft_warn") toast.message("Soft warnings on this generation — see the quality panel");
      else toast.success("Subject generated");
      onValueChange({
        source: "generate",
        storage_path: j.storage_path,
        realism_verdict: j.realism_verdict,
        auto_cutout: true,
      });
    } catch (e) {
      toast.error(e?.message || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const [bgOptions, setBgOptions] = useState([]);
  const [bgIdx, setBgIdx] = useState(-1);
  const [bgBusy, setBgBusy] = useState(false);

  // Abstract backgrounds (procedural + AI) — background slots only.
  const [abstractOpts, setAbstractOpts] = useState([]);
  const [abstractBusy, setAbstractBusy] = useState(false);
  const [abstractAiBusy, setAbstractAiBusy] = useState(false);
  const loadAbstract = async () => {
    setAbstractBusy(true);
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/cards/${cardId}/abstract/options`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ count: 6 }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || "Failed");
      setAbstractOpts(d.options || []);
    } catch (e) {
      toast.error(e?.message || "Couldn't generate abstract backgrounds");
    } finally {
      setAbstractBusy(false);
    }
  };
  const genAbstractAI = async () => {
    setAbstractAiBusy(true);
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/cards/${cardId}/abstract/ai`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || "AI generation failed");
      if (d.option) setAbstractOpts((prev) => [d.option, ...prev]);
      toast.success("AI abstract added");
    } catch (e) {
      toast.error(e?.message || "AI abstract failed");
    } finally {
      setAbstractAiBusy(false);
    }
  };

  // Save the chosen background into the reusable brand backgrounds pool.
  const [savingLib, setSavingLib] = useState(false);
  const saveToLibrary = async () => {
    const sp = currentValue?.storage_path;
    if (!sp) return toast.error("Pick a background first");
    setSavingLib(true);
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/brand/backgrounds/save`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ storage_path: sp, label: "Saved background" }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || "Save failed");
      toast.success("Saved to library — available for every post");
    } catch (e) {
      toast.error(e?.message || "Couldn't save to library");
    } finally {
      setSavingLib(false);
    }
  };

  const shuffleBackground = async () => {
    setBgBusy(true);
    try {
      let opts = bgOptions;
      if (!opts.length) {
        const r = await fetch(`${API}/v2/workspaces/${workspaceId}/brand/background-options`, { credentials: "include" });
        const d = await r.json();
        opts = d.options || [];
        setBgOptions(opts);
      }
      if (!opts.length) {
        toast.message("No on-brand backgrounds yet — add some via Stock or Upload");
        return;
      }
      const next = (bgIdx + 1) % opts.length;
      setBgIdx(next);
      const o = opts[next];
      setVal({ source: "preset", storage_path: o.storage_path, asset_id: o.asset_id });
      toast.success(`Background: ${o.label} (${next + 1}/${opts.length})`);
    } catch {
      toast.error("Couldn't load backgrounds");
    } finally {
      setBgBusy(false);
    }
  };

  return (
    <div className="mt-2">
      {!isCutout && (
        <Button
          data-testid={`slot-${slot.id}-shuffle-bg`}
          variant="outline"
          size="sm"
          className="w-full mb-2"
          onClick={shuffleBackground}
          disabled={bgBusy}
        >
          {bgBusy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Shuffle className="h-3 w-3 mr-1" />}
          Shuffle on-brand background
        </Button>
      )}
      <Tabs value={tab} onValueChange={setTab}>
      <TabsList className="w-full grid grid-cols-4">
        <TabsTrigger value="library" data-testid={`slot-${slot.id}-tab-library`}>
          <ImageIcon className="h-3 w-3 mr-1" /> Library
        </TabsTrigger>
        <TabsTrigger value="stock" data-testid={`slot-${slot.id}-tab-stock`}>
          <Search className="h-3 w-3 mr-1" /> Stock
        </TabsTrigger>
        <TabsTrigger value="upload" data-testid={`slot-${slot.id}-tab-upload`}>
          <Upload className="h-3 w-3 mr-1" /> Upload
        </TabsTrigger>
        {isCutout ? (
          <TabsTrigger value="generate" data-testid={`slot-${slot.id}-tab-generate`} className="opacity-70">
            <Sparkles className="h-3 w-3 mr-1" /> Generate
          </TabsTrigger>
        ) : (
          <TabsTrigger value="abstract" data-testid={`slot-${slot.id}-tab-abstract`}>
            <Sparkles className="h-3 w-3 mr-1" /> Abstract
          </TabsTrigger>
        )}
      </TabsList>
      <TabsContent value="library" className="pt-2">
        {libraryAssets.length ? (
          <div className="grid grid-cols-3 gap-1">
            {libraryAssets.map((a) => (
              <div key={a.asset_id} className="relative group aspect-square">
                <button
                  onClick={() => setVal({ source: "library", asset_id: a.asset_id, storage_path: a.storage_path })}
                  className="absolute inset-0 bg-muted rounded overflow-hidden hover:opacity-80"
                  data-testid={`library-asset-${a.asset_id}`}
                  title={a.tags?.[0] || "asset"}
                >
                  <img
                    alt={a.tags?.[0] || "asset"}
                    src={`${API}/v2/workspaces/${workspaceId}/brand/assets/${a.asset_id}/thumbnail`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = "none"; const fb = e.currentTarget.nextElementSibling; if (fb) fb.style.display = "flex"; }}
                  />
                  <div className="w-full h-full items-center justify-center bg-slate-100 text-slate-400" style={{ display: "none" }}>
                    <ImageOff className="h-6 w-6" />
                  </div>
                </button>
                {!isCutout ? (
                  <div className="absolute top-0.5 right-0.5 flex gap-0.5 transition">
                    <button
                      type="button"
                      data-testid={`library-asset-rename-${a.asset_id}`}
                      onClick={(e) => { e.stopPropagation(); renameLibraryAsset(a.asset_id, a.tags?.[0]); }}
                      className="h-5 w-5 grid place-items-center rounded bg-white/90 text-slate-700 hover:bg-white shadow"
                      title="Rename"
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      data-testid={`library-asset-delete-${a.asset_id}`}
                      onClick={(e) => { e.stopPropagation(); deleteLibraryAsset(a.asset_id); }}
                      className="h-5 w-5 grid place-items-center rounded bg-white/90 text-rose-600 hover:bg-white shadow"
                      title="Remove from library"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground italic">
            No library {isCutout ? "subjects" : "backgrounds"} yet. Use Upload/Stock/Abstract, then "Save to library".
          </p>
        )}
      </TabsContent>
      <TabsContent value="stock" className="pt-2 space-y-2">
        <div className="flex gap-1">
          <Input
            data-testid={`slot-${slot.id}-stock-query`}
            placeholder={isCutout ? "Search a subject (e.g. 'business woman portrait')" : "Search a background (e.g. 'blue gradient texture')"}
            value={stockQuery}
            onChange={(e) => setStockQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") runStockSearch(); }}
          />
          <Button
            data-testid={`slot-${slot.id}-stock-search-btn`}
            size="sm"
            onClick={runStockSearch}
            disabled={stockLoading || !stockQuery.trim()}
          >
            {stockLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Search className="h-3 w-3" />}
          </Button>
        </div>
        {stockResults.length ? (
          <div className="grid grid-cols-3 gap-1">
            {stockResults.map((c) => (
              <button
                key={`${c.source}-${c.source_id}`}
                onClick={() => pickStock(c)}
                disabled={!!stockPicking}
                title={c.attribution_text}
                className="relative aspect-square bg-muted rounded overflow-hidden hover:opacity-80 disabled:opacity-50"
                data-testid={`stock-result-${c.source}-${c.source_id}`}
              >
                <img alt={c.description || "stock"} src={c.thumb_url} className="w-full h-full object-cover" />
                {stockPicking === c.source_id ? (
                  <span className="absolute inset-0 grid place-items-center bg-black/40">
                    <Loader2 className="h-4 w-4 animate-spin text-white" />
                  </span>
                ) : null}
                <span className="absolute bottom-0 left-0 right-0 text-[8px] bg-black/50 text-white px-1 truncate">{c.source}</span>
              </button>
            ))}
          </div>
        ) : (
          <p className="text-[11px] text-muted-foreground italic">
            Search Unsplash + Pexels. {isCutout ? "The subject's background is removed automatically." : "Used full-bleed as the post background."}
          </p>
        )}
      </TabsContent>
      <TabsContent value="upload" className="pt-2">
        <Input
          data-testid={`slot-${slot.id}-upload-input`}
          type="file"
          accept="image/*"
          onChange={(e) => onUpload(e.target.files?.[0])}
        />
      </TabsContent>
      {isCutout ? (
      <TabsContent value="generate" className="pt-2 space-y-2">
        <Textarea
          data-testid={`slot-${slot.id}-generate-brief`}
          placeholder="Describe the subject (e.g. 'confident woman in navy blazer, head and shoulders portrait')"
          value={subjectBrief}
          onChange={(e) => setSubjectBrief(e.target.value)}
          rows={2}
        />
        <Button
          data-testid={`slot-${slot.id}-generate-btn`}
          size="sm"
          onClick={onGenerate}
          disabled={generating || !subjectBrief.trim()}
        >
          {generating ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Sparkles className="h-3 w-3 mr-1" />}
          Generate subject
        </Button>
        <p className="text-[11px] text-muted-foreground italic">
          Library and upload are preferred. AI generation runs the realism gate.
        </p>
      </TabsContent>
      ) : (
      <TabsContent value="abstract" className="pt-2 space-y-2">
        <div className="flex gap-1">
          <Button
            data-testid={`slot-${slot.id}-abstract-options-btn`}
            size="sm" variant="outline" className="flex-1"
            onClick={loadAbstract} disabled={abstractBusy}
          >
            {abstractBusy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Shuffle className="h-3 w-3 mr-1" />}
            {abstractOpts.length ? "New options" : "Generate options"}
          </Button>
          <Button
            data-testid={`slot-${slot.id}-abstract-ai-btn`}
            size="sm" variant="outline"
            onClick={genAbstractAI} disabled={abstractAiBusy}
            title="AI abstract (Nano Banana) — uses credits"
          >
            {abstractAiBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
          </Button>
        </div>
        {abstractOpts.length ? (
          <div className="grid grid-cols-3 gap-1">
            {abstractOpts.map((o, i) => (
              <button
                key={`${o.style}-${i}`}
                onClick={() => setVal({ source: "abstract", storage_path: o.storage_path })}
                className="relative aspect-square bg-muted rounded overflow-hidden hover:opacity-80"
                data-testid={`slot-${slot.id}-abstract-option-${i}`}
                title={o.label}
              >
                <img alt={o.label} src={o.url} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        ) : (
          <p className="text-[11px] text-muted-foreground italic">
            Instant on-brand abstract backgrounds — or generate an AI one (sparkle).
          </p>
        )}
      </TabsContent>
      )}
    </Tabs>
    {!isCutout && currentValue?.storage_path ? (
      <Button
        data-testid={`slot-${slot.id}-save-to-library-btn`}
        size="sm" variant="ghost" className="w-full mt-2 text-xs"
        onClick={saveToLibrary} disabled={savingLib}
      >
        {savingLib ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <ImageIcon className="h-3 w-3 mr-1" />}
        Save this background to the library
      </Button>
    ) : null}
    {isCutout ? (
      <>
      <div className="mt-2" data-testid={`slot-${slot.id}-recolor`}>
        <div className="text-[11px] text-muted-foreground mb-1">Subject look</div>
        <div className="grid grid-cols-4 gap-1">
          {[
            { key: "original", label: "Original" },
            { key: "bw", label: "B&W" },
            { key: "duotone", label: "Duotone" },
            { key: "brand_tint", label: "Brand" },
          ].map((opt) => {
            const active = (currentValue?.recolor || "original") === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                data-testid={`slot-${slot.id}-recolor-${opt.key}`}
                onClick={() => setVal({ ...currentValue, recolor: opt.key })}
                className={`rounded-md border px-2 py-1 text-[11px] transition ${
                  active
                    ? "border-blue-600 bg-blue-50 text-blue-900"
                    : "border-border text-muted-foreground hover:border-blue-300"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
      {currentValue?.storage_path ? (
        <div className="mt-2 space-y-1.5" data-testid={`slot-${slot.id}-vector-shadow`}>
          <label className="flex items-center gap-2 text-[11px] text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              data-testid={`slot-${slot.id}-skip-cutout`}
              checked={currentValue?.auto_cutout === false}
              onChange={(e) => setVal({ ...currentValue, auto_cutout: !e.target.checked })}
            />
            Already a cut-out / logo (skip background removal)
          </label>
          <label className="flex items-center gap-2 text-[11px] text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              data-testid={`slot-${slot.id}-shadow`}
              checked={!!currentValue?.shadow}
              onChange={(e) => setVal({ ...currentValue, shadow: e.target.checked })}
            />
            Drop shadow
          </label>
        </div>
      ) : null}
      {currentValue?.storage_path ? (
        <div className="mt-3" data-testid={`slot-${slot.id}-subject-controls`}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[11px] text-muted-foreground">Subject size</span>
            <button
              type="button"
              data-testid={`slot-${slot.id}-subject-reset`}
              className="text-[10px] text-blue-600 hover:underline"
              onClick={() => setVal({ ...currentValue, subject_x: undefined, subject_y: undefined, subject_scale: undefined })}
            >
              Reset
            </button>
          </div>
          <input
            type="range" min="0.4" max="2.5" step="0.05"
            data-testid={`slot-${slot.id}-subject-scale`}
            value={currentValue?.subject_scale ?? 1.0}
            onChange={(e) => setVal({ ...currentValue, subject_scale: parseFloat(e.target.value) })}
            className="w-full"
          />
          <div className="text-[11px] text-muted-foreground mt-2 mb-1">Move subject</div>
          <div className="grid grid-cols-3 gap-1 w-28">
            <span />
            <button type="button" data-testid={`slot-${slot.id}-subject-up`} className="rounded border p-1 hover:bg-muted" onClick={() => nudgeSubject(0, -0.04)}>▲</button>
            <span />
            <button type="button" data-testid={`slot-${slot.id}-subject-left`} className="rounded border p-1 hover:bg-muted" onClick={() => nudgeSubject(-0.04, 0)}>◀</button>
            <span />
            <button type="button" data-testid={`slot-${slot.id}-subject-right`} className="rounded border p-1 hover:bg-muted" onClick={() => nudgeSubject(0.04, 0)}>▶</button>
            <span />
            <button type="button" data-testid={`slot-${slot.id}-subject-down`} className="rounded border p-1 hover:bg-muted" onClick={() => nudgeSubject(0, 0.04)}>▼</button>
            <span />
          </div>
        </div>
      ) : null}
      </>
    ) : null}
    </div>
  );
}
